import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  locationCastColumnGroupBounds,
  locationCastColumnLabelBounds,
} from "../LocationLayout/bounds";
import type { BoardModelDebuggable } from "../../board/BoardModelDebug/debuggable";
import {
  locationRunPrimaryDebuggable,
  locationRunSecondaryDebuggable,
} from "../LocationRun/debug";
import type { ActiveLocationGroup } from "./index";
import {
  characterIdsInActiveLocationGroup,
  onStagePrimaryRunId,
  onStageRunIds,
  onStageSecondaryRunIds,
  visiblePartRunIds,
} from "./index";

export interface OnStageLocationDebugSpec {
  labelsId: string;
  locationFrameId: string;
  onStageRunIds: readonly string[];
  characterIds: readonly string[];
  primaryRunId?: string;
  onStageSecondaryRunIds: readonly string[];
  visiblePartRunIds: readonly string[];
}

function locationGroupDebuggable(
  debugId: string,
  onStageRunIdsList: readonly string[],
): BoardModelDebuggable {
  return {
    kind: "LocationGroup",
    debugId,
    measure: (context) =>
      locationCastColumnLabelBounds(
        context.world,
        context.locationsRoot,
        onStageRunIdsList,
      ),
  };
}

function locationGroupOutlineDebuggable(
  spec: OnStageLocationDebugSpec,
): BoardModelDebuggable {
  return {
    kind: "Location",
    debugId: spec.locationFrameId,
    measure: (context) =>
      locationCastColumnGroupBounds(
        context.world,
        context.labelsRoot,
        context.locationsRoot,
        spec.characterIds,
        spec.primaryRunId,
        spec.onStageSecondaryRunIds,
      ),
  };
}

/** On-stage + part-run chips for one site column (site or open-rail trunk). */
export function debuggablesForOnStageLocationStack(
  spec: OnStageLocationDebugSpec,
): BoardModelDebuggable[] {
  if (spec.onStageRunIds.length === 0) {
    return [];
  }
  const out: BoardModelDebuggable[] = [
    locationGroupDebuggable(spec.labelsId, spec.onStageRunIds),
    locationGroupOutlineDebuggable(spec),
  ];
  if (spec.primaryRunId !== undefined) {
    out.push(locationRunPrimaryDebuggable(spec.primaryRunId));
  }
  for (const runId of spec.visiblePartRunIds) {
    out.push(locationRunSecondaryDebuggable(runId));
  }
  return out;
}

/** All board-model outlines owned by this playhead location group. */
export function debuggablesForActiveLocationGroup(
  group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): BoardModelDebuggable[] {
  const onStageRuns = onStageRunIds(group, layout, playheadMs);
  return debuggablesForOnStageLocationStack({
    labelsId: `${group.id}:labels`,
    locationFrameId: group.id,
    onStageRunIds: onStageRuns,
    characterIds: characterIdsInActiveLocationGroup(
      layout.rail,
      group,
      playheadMs,
    ),
    primaryRunId: onStagePrimaryRunId(group, layout, playheadMs),
    onStageSecondaryRunIds: onStageSecondaryRunIds(
      group,
      layout,
      playheadMs,
    ),
    visiblePartRunIds: visiblePartRunIds(group),
  });
}
